import type { Params } from "@solidjs/router";
import { useChatClient } from "@storytell/cp-client";
import { createConversationRequest, useCallOptions, withBearerToken } from "@storytell/cp-client";
import type { StreamingChatId, StreamingService } from "@storytell/streaming";
import { createMemo, createResource } from "solid-js";
import { isServer } from "solid-js/web";
import type { ChatServiceState, SetChatServiceState } from "./chatService";

export const createChatServiceResource = (deps: {
  authReady: () => boolean;
  authToken: () => string | undefined;
  urlParams: () => Params;
  state: ChatServiceState;
  setState: SetChatServiceState;
  stream: StreamingService;
}) => {
  const chatClient = useChatClient();

  const canFetch = () => (deps.authReady() ? ([deps.urlParams().chatId] as const) : undefined);

  const [resource] = createResource(
    canFetch,
    async ([chatId]) => {
      try {
        // If we're streaming, we don't need to fetch the chat
        if (deps.state.isStreaming) return;

        if (!chatId) {
          console.log("chatService: chatId is undefined, skipping resource fetch");
          deps.setState({
            isStreaming: false,
            chatId: undefined,
            error: undefined,
          });
          return;
        }
        deps.setState("chatId", chatId as StreamingChatId);

        const conversation = await chatClient.conversation(
          createConversationRequest({
            chatId: chatId,
            recentMessagesCount: BigInt(20),
          }),
          useCallOptions(withBearerToken(deps.authToken())),
        );

        if (conversation.isOk()) {
          console.log("chatService: fetched chat:", { chatId });
          const data = conversation.value;
          return { type: "success", data } as const;
        }

        console.error("chatService: error fetching chat:", { chatId, error: conversation.error });
        return { type: "error", error: conversation.error } as const;
      } catch (error) {
        console.error("chatService: error fetching chat:", { chatId, error });
        return { type: "error", error } as const;
      }
    },
    {
      deferStream: true,
    },
  );
  try {
    if (!isServer && resource.latest !== undefined) {
      // Hydrate the SSR chat recording into the streaming service
      if (resource.latest.type === "success") {
        console.log("chatService: hydrating chat:", { chatId: resource.latest.data.chatId });
        deps.stream.chat.processChatRecording(resource.latest.data);
      }
    }
  } catch (error) {
    console.error("Error hydrating chat recording", error);
  }

  return resource;
};
